import {useNotification} from '@hconnect/uikit/src/common'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useMemo} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {queries} from '@settings/api/queries'
import {invalidateSettingsQuery} from '@settings/modules/common/providers'
import {ErrorKey} from '@settings/modules/common/types'
import {notifyIfErrorMessage} from '@settings/modules/common/utils'
import {useUrlParam} from '@settings/modules/routing'

const getExceptionDuringValidationMessage = (t: (key: string) => string, plantCode: string) =>
  `${t('error.exceptionDuringValidation')} ${plantCode}`

export const useSentDateToVerifyFormula = () => {
  const {notify} = useNotification()
  const raiseError = useErrorHandler()
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const errorTranslations = useMemo(
    () => ({
      [ErrorKey.ERR_SENSOR_DATA_NOT_AVAILABLE]: t('error.sensorDataNotAvailable'),
      [ErrorKey.ERR_DIVISION_BY_ZERO]: t('error.divisionByZero'),
      [ErrorKey.ERR_SENSOR_VALUES_EXCEED_THRESHOLD]: t('error.sensorValuesExceedThreshold'),
      [ErrorKey.ERR_FUNCTION_DOES_NOT_EXIST]: t('error.functionDoesNotExist'),
      [ErrorKey.ERR_SYNTAX_ERROR]: t('error.syntaxError'),
      [ErrorKey.ERR_UNSUPPORTED_BINARY_OPERATION]: t('error.unsupportedBinaryOperation'),
      [ErrorKey.ERR_UNSUPPORTED_UNARY_OPERATION]: t('error.unsupportedUnaryOperation'),
      [ErrorKey.ERR_UNDEFINED_VARIABLE_OR_CONSTANT]: t('error.undefinedVariableOrConstant'),
      [ErrorKey.ERR_INVALID_NUMBER_OF_ARGUMENTS]: t('error.invalidNumberOfArguments')
    }),
    [t]
  )

  return useMutation<
    Awaited<ReturnType<typeof queries.sentDateToVerifyFormula>>,
    AxiosError<ErrorKey> | undefined,
    Parameters<typeof queries.sentDateToVerifyFormula>[0]
  >({
    mutationFn: queries.sentDateToVerifyFormula,
    onError: (error) => {
      const errorKey = error?.response?.data
      const message =
        errorKey && errorKey in errorTranslations
          ? errorTranslations[errorKey]
          : getExceptionDuringValidationMessage(t, plantCode)
      notifyIfErrorMessage(message, raiseError, notify)
    },
    onSuccess: () => {
      notify('success', t('success.verifyKpiFormula'))
    },
    onSettled: () => {
      invalidateSettingsQuery('kpis')
    }
  })
}
